import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

/**
 * Whether the visitor is signed in to the shop.
 *
 * Avery Smart Studio has to know this before it can offer to keep a QR code
 * for someone — a customer already signed in should be told so, not asked to
 * sign in again.
 *
 * Only the flag crosses to the browser. Which customer it is stays on the
 * server, so nothing here can be edited into a claim of being somebody else;
 * every decision about what may be seen or changed is taken in Drupal, against
 * the session. The sign-in URL is read from the answer rather than assembled
 * here, so the studio never has to know where the shop lives.
 */

const ENDPOINT = '/shop-identity/status';

export const fetchShopIdentity = createAsyncThunk(
  'shopIdentity/fetch',
  async (_arg, { rejectWithValue }) => {
    let response;
    try {
      response = await fetch(ENDPOINT, {
        // The shop's cookies are what identify the visitor, so they have to
        // travel.
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch (e) {
      return rejectWithValue('unreachable');
    }

    if (!response.ok) {
      return rejectWithValue('unreachable');
    }

    const payload = await response.json();

    // Drupal answered, but it could not get an answer from the shop. Treated
    // the same as Drupal itself failing: the studio must not tell somebody to
    // sign in when the sign-in cannot work.
    if (payload.shop_unavailable) {
      return rejectWithValue('unreachable');
    }

    return {
      authenticated: Boolean(payload.authenticated),
      loginUrl: payload.login_url || null,
    };
  },
);

// Deliberately no `condition` guard against a concurrent second dispatch.
// It would be tempting — the studio asks when it opens and the sign-in frame
// asks again after each step — but a skipped thunk rejects with a
// ConditionError, which the frame would have to tell apart from a shop it
// could not reach. The two moments are far apart in practice, and one extra
// request costs less than that branch.

const initialState = {
  isFetching: false,
  isError: false,
  isSuccess: false,
  // Never true until the shop has actually said so: not knowing has to read as
  // signed out, or a customer who is not signed in would be offered a save
  // that cannot work.
  authenticated: false,
  loginUrl: null,
};

const shopIdentitySlice = createSlice({
  name: 'shopIdentity',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchShopIdentity.pending, (state) => {
      state.isFetching = true;
      state.isError = false;
      // isSuccess is deliberately left alone: a previously known answer stays
      // on screen while the next one is fetched, so nobody sees "not signed
      // in" flash at them right after signing in.
    });

    builder.addCase(fetchShopIdentity.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.isSuccess = true;
      state.authenticated = payload.authenticated;
      state.loginUrl = payload.loginUrl;
    });

    builder.addCase(fetchShopIdentity.rejected, (state) => {
      state.isFetching = false;
      state.isError = true;
      // A shop we could not reach is not a visitor who is signed out, so the
      // flag is left as it was and the studio says nothing rather than telling
      // somebody to sign in when they already have.
    });
  },
});

export const shopIdentitySelector = (state) => state.shopIdentity;

export default shopIdentitySlice.reducer;
